import {
  ClosedApplicationTile,
  DecisionningErrorTile,
  ErrorPage,
  Loader,
  useGetCreditApplicationById,
  useGetSkipBankRequestRequirements,
  useSubmissionProgress,
} from '@applyonline/shared'
import { scrollToTop } from '@applyonline/shared/utils'
import { ECreditApplicationStatus, EFinancingProgram } from '@if/core/types'
import { useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useRequiredDocumentsWithStatus } from '../../data/credit-application-hooks'
import { extractNumbersFromMerchantId } from '../../data/merchant-selectors'
import AgentContactPage from '../CancelApplication/AgentContactPage'
import CompletedAppPage from './CompletedApp/CompletedAppPage'
import HardHitConsentNoCreditReportPage from './components/HardHitConsentNoCreditReportPage'
import MultipleCreditReportsFound from './components/MultipleCreditReportsFound'
import { canPollCreditApp, computeHighLevelPage, isServerAnalysingCredit } from './credit-hooks'
import HardHitApprovedResultPage from './HardHitApprovedResultPage'
import HardHitBanResultPage from './HardHitBan'
import HardHitUnapprovedResultPage from './HardHitUnapprovedResultPage'
import InitialApprobationForm from './InitialApprobationPage/InitialApprobationForm'
import PrequalificationResultPage from './PrequalResultPage'

const RouteToStatePage = () => {
  const { t } = useTranslation()
  const allParams = useParams()
  const id = allParams.id!
  const [shouldPoll, setShouldPoll] = useState(false)
  const [creditApp, isFetchingCreditApp] = useGetCreditApplicationById(id, EFinancingProgram.Personal, shouldPoll) // if true, poll every 5 secs
  const [skipBankRequiredDocuments, isFetchingReqDocsForSkip] = useGetSkipBankRequestRequirements(
    id,
    EFinancingProgram.Personal,
  )
  const skipBankRequiredDocumentsWithStatus = useRequiredDocumentsWithStatus(
    skipBankRequiredDocuments,
    creditApp?.documents,
  )
  const highLevelPage = computeHighLevelPage(creditApp, isFetchingCreditApp)
  const { progress, messageKey } = useSubmissionProgress(creditApp)

  useEffect(() => {
    if (creditApp) {
      const should = isServerAnalysingCredit(creditApp) && canPollCreditApp(creditApp)
      setShouldPoll(should)
    }
  }, [creditApp])

  useLayoutEffect(scrollToTop, [])

  if (isFetchingReqDocsForSkip || (!creditApp && isFetchingCreditApp) || highLevelPage === 'loading') {
    return (
      <div>
        <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />
      </div>
    )
  }

  const creditAppStatus =
    (creditApp?.status as ECreditApplicationStatus.Completed | ECreditApplicationStatus.Cancelled) ??
    ECreditApplicationStatus.Cancelled
  const merchantId = creditApp ? extractNumbersFromMerchantId(creditApp.merchantId) : ''
  const targetPath = `/${merchantId}`

  return (
    <>
      {highLevelPage === 'notFound' && <ErrorPage title={t('common.notFound')} />}

      {highLevelPage === 'analysis-running' && (
        <div>
          <Loader title={t('finalDecision.processing')} description={t(messageKey as 'loader.submitting')} progression={progress} />
        </div>
      )}

      {highLevelPage === 'cancelling' && <AgentContactPage />}

      {highLevelPage === 'prequal-result' && <PrequalificationResultPage creditApp={creditApp!} />}

      {highLevelPage === 'prequalRefuseBan' && <HardHitBanResultPage />}

      {highLevelPage === 'multipleCreditReportsFound' && <MultipleCreditReportsFound creditApp={creditApp!} />}
      {highLevelPage === 'decisionning-error' && <DecisionningErrorTile />}

      {highLevelPage === 'first-approval' && <InitialApprobationForm creditApp={creditApp!} />}

      {highLevelPage === 'hardhit-result' && <HardHitUnapprovedResultPage creditApp={creditApp!} />}

      {highLevelPage === 'approved' && (
        <HardHitApprovedResultPage
          creditApp={creditApp!}
          skipBankAccountRequiredDocuments={skipBankRequiredDocumentsWithStatus}
        />
      )}
      {highLevelPage === 'hardHit-NoCreditReport' && <HardHitConsentNoCreditReportPage creditApp={creditApp!} />}
      {highLevelPage === 'closed' && <ClosedApplicationTile status={creditAppStatus} targetPath={targetPath} />}

      {highLevelPage === 'funding' && <CompletedAppPage creditApp={creditApp!} />}
    </>
  )
}

export default RouteToStatePage
